import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import {
  createManagedUser,
  deleteManagedUser,
  listManagedUsers,
  updateManagedUser,
} from "./services/userManagementService";
import {
  ManagedUser,
  ManagedUserInput,
  UserRole,
} from "./types/userManagement";

type EditorState =
  | { mode: "create"; user?: never; values: ManagedUserInput }
  | { mode: "edit"; user: ManagedUser; values: ManagedUserInput };

const roleOptions: UserRole[] = ["administrator", "doctor", "nurse"];

const getRoleColor = (
  role: UserRole,
): "default" | "primary" | "success" | "warning" => {
  switch (role) {
    case "administrator":
      return "primary";
    case "doctor":
      return "success";
    case "nurse":
      return "warning";
    default:
      return "default";
  }
};

const emptyInput = (): ManagedUserInput => ({
  username: "",
  displayName: "",
  email: "",
  role: "doctor",
  isActive: true,
});

const normalizeInput = (values: ManagedUserInput): ManagedUserInput => ({
  ...values,
  username: values.username.trim(),
  displayName: values.displayName.trim(),
  email: values.email.trim(),
});

const canManageUsers = (): boolean => {
  const raw = localStorage.getItem("user");
  if (!raw) return true;
  try {
    const u = JSON.parse(raw);
    const role = String(u?.role || u?.userRole || "").toLowerCase();
    return role === "administrator" || role === "admin";
  } catch {
    return true;
  }
};

const UserManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editor, setEditor] = useState<EditorState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ManagedUser | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const allowManage = useMemo(() => canManageUsers(), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listManagedUsers();
      setRows(data);
    } catch (e: any) {
      setError(e?.message || t("userManagement.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "username",
        headerName: t("userManagement.fields.username"),
        flex: 1,
        minWidth: 160,
      },
      {
        field: "displayName",
        headerName: t("userManagement.fields.displayName"),
        flex: 1,
        minWidth: 200,
      },
      {
        field: "email",
        headerName: t("userManagement.fields.email"),
        flex: 1,
        minWidth: 220,
      },
      {
        field: "role",
        headerName: t("userManagement.fields.role"),
        minWidth: 160,
        renderCell: (params) => {
          const role = params.value as UserRole;
          return (
            <Chip
              size="small"
              color={getRoleColor(role)}
              label={t(`userManagement.roles.${role}`)}
              variant="outlined"
            />
          );
        },
      },
      {
        field: "isActive",
        headerName: t("userManagement.fields.active"),
        minWidth: 120,
        type: "boolean",
      },
      {
        field: "updatedAt",
        headerName: "Updated",
        minWidth: 180,
        valueFormatter: (value: any) => {
          if (!value) return "-";
          const d = new Date(String(value));
          return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
        },
      },
    ],
    [t],
  );

  const openCreate = () => setEditor({ mode: "create", values: emptyInput() });

  const openEdit = (user: ManagedUser) =>
    setEditor({
      mode: "edit",
      user,
      values: {
        username: user.username,
        displayName: user.displayName,
        email: user.email,
        role: user.role,
        isActive: user.isActive,
      },
    });

  const handleSave = async () => {
    if (!editor) return;
    const values = normalizeInput(editor.values);

    if (!values.username || !values.displayName || !values.email) {
      setSnack({
        open: true,
        message: t("userManagement.error"),
        severity: "error",
      });
      return;
    }

    try {
      if (editor.mode === "create") {
        await createManagedUser(values);
      } else {
        await updateManagedUser(editor.user.id, values);
      }
      setEditor(null);
      setSnack({
        open: true,
        message: t("userManagement.saved"),
        severity: "success",
      });
      await load();
    } catch (e: any) {
      setSnack({
        open: true,
        message: e?.message || t("userManagement.error"),
        severity: "error",
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteManagedUser(deleteTarget.id);
      setDeleteTarget(null);
      setSnack({
        open: true,
        message: t("userManagement.saved"),
        severity: "success",
      });
      await load();
    } catch (e: any) {
      setSnack({
        open: true,
        message: e?.message || t("userManagement.error"),
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          {t("userManagement.title")}
        </Typography>
        <Button
          variant="contained"
          onClick={openCreate}
          disabled={!allowManage}
        >
          {t("userManagement.add")}
        </Button>
      </Box>

      {!allowManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("userManagement.adminOnly")}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ height: 520, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar }}
          initialState={{
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
          }}
          pageSizeOptions={[5, 10, 25]}
          onRowDoubleClick={(params) =>
            allowManage && openEdit(params.row as ManagedUser)
          }
          onRowClick={(params) =>
            setSelectedId(String((params.row as ManagedUser).id))
          }
          slotProps={{
            toolbar: { showQuickFilter: true },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
        <Button variant="outlined" onClick={load}>
          {t("common.refresh")}
        </Button>
        <Button
          variant="outlined"
          disabled={!allowManage || !selectedId}
          onClick={() => {
            const user = rows.find((r) => r.id === selectedId);
            if (user) openEdit(user);
          }}
        >
          {t("userManagement.edit")}
        </Button>
        <Button
          variant="outlined"
          color="error"
          disabled={!allowManage || !selectedId}
          onClick={() => {
            const user = rows.find((r) => r.id === selectedId);
            if (user) setDeleteTarget(user);
          }}
        >
          {t("userManagement.delete")}
        </Button>
      </Box>

      {/* Editor dialog */}
      <Dialog
        open={!!editor}
        onClose={() => setEditor(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editor?.mode === "create"
            ? t("userManagement.add")
            : t("userManagement.edit")}
        </DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 2 }}>
          <TextField
            label={t("userManagement.fields.username")}
            value={editor?.values.username || ""}
            onChange={(e) =>
              setEditor((prev) =>
                prev
                  ? {
                      ...prev,
                      values: { ...prev.values, username: e.target.value },
                    }
                  : prev,
              )
            }
            fullWidth
            disabled={!allowManage}
          />
          <TextField
            label={t("userManagement.fields.displayName")}
            value={editor?.values.displayName || ""}
            onChange={(e) =>
              setEditor((prev) =>
                prev
                  ? {
                      ...prev,
                      values: { ...prev.values, displayName: e.target.value },
                    }
                  : prev,
              )
            }
            fullWidth
            disabled={!allowManage}
          />
          <TextField
            label={t("userManagement.fields.email")}
            value={editor?.values.email || ""}
            onChange={(e) =>
              setEditor((prev) =>
                prev
                  ? {
                      ...prev,
                      values: { ...prev.values, email: e.target.value },
                    }
                  : prev,
              )
            }
            fullWidth
            disabled={!allowManage}
          />
          <FormControl fullWidth disabled={!allowManage}>
            <InputLabel>{t("userManagement.fields.role")}</InputLabel>
            <Select
              label={t("userManagement.fields.role")}
              value={editor?.values.role || "doctor"}
              onChange={(e) =>
                setEditor((prev) =>
                  prev
                    ? {
                        ...prev,
                        values: {
                          ...prev.values,
                          role: e.target.value as UserRole,
                        },
                      }
                    : prev,
                )
              }
            >
              {roleOptions.map((role) => (
                <MenuItem key={role} value={role}>
                  {t(`userManagement.roles.${role}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={!!editor?.values.isActive}
                onChange={(e) =>
                  setEditor((prev) =>
                    prev
                      ? {
                          ...prev,
                          values: {
                            ...prev.values,
                            isActive: e.target.checked,
                          },
                        }
                      : prev,
                  )
                }
              />
            }
            label={t("userManagement.fields.active")}
            disabled={!allowManage}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditor(null)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!allowManage}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("userManagement.delete")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            {t("userManagement.confirmDelete")}
          </Typography>
          {deleteTarget && (
            <Box sx={{ mt: 2 }}>
              <Typography fontWeight={700}>
                {deleteTarget.displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {deleteTarget.username} •{" "}
                {t(`userManagement.roles.${deleteTarget.role}`)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={!allowManage}
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagementPage;
